import { RevampTable, TableCell, TableHeader } from '@design/components/table';
import clsx from 'clsx';
import { Fragment, useEffect, useRef, useState } from 'react';

import styles from './ColumnTable.module.scss';
import type { ColumnTableProps } from './ColumnTable.types';
import { ColumnTableCell } from './ColumnTableCell';

/**
 * Config-driven table: pass `columns` + `data` and this renders the
 * `<thead>`/`<tbody>` for you — per-column `render`/`renderHeader`/`renderCell`,
 * sticky columns, sticky header, hover, loading rows, a row action column, and
 * row click. Renders the design-system `RevampTable` in composition mode
 * internally, so `RevampTable` stays the single styled table.
 */
export const ColumnTable = <T extends {}>({
  columns,
  data,
  className,
  alignment = 'left',
  withBodyRowHover = true,
  isHeaderSticky = false,
  onRowClick,
  renderActionColumn,
}: ColumnTableProps<T>) => {
  const ref = useRef<HTMLTableElement>(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);

  useEffect(() => {
    const parent = ref.current?.parentElement;
    setHasScrollbar(!!parent && parent.clientWidth < parent.scrollWidth);
  }, [data, columns]);

  return (
    <RevampTable
      ref={ref}
      className={clsx(
        styles.table,
        isHeaderSticky && styles.headerSticky,
        className
      )}
      data-revamp-table
      withBodyRowHover={withBodyRowHover}
      withBodyRowCursor={!!onRowClick}
    >
      <thead>
        <tr>
          {columns.map(column => {
            const columnAlignment = column.alignment || alignment;
            const content = column.renderHeader
              ? column.renderHeader()
              : column.label;

            return column.sticky ? (
              <TableHeader
                className={clsx(styles.stickyTableHeader, {
                  [styles.stickyTableHeaderBorder]: hasScrollbar,
                })}
                alignment={columnAlignment}
                isHoverEnabled={false}
                key={column.key}
              >
                {content}
              </TableHeader>
            ) : (
              <TableHeader
                alignment={columnAlignment}
                isHoverEnabled={false}
                key={column.key}
              >
                {content}
              </TableHeader>
            );
          })}
          {renderActionColumn && <TableHeader />}
        </tr>
      </thead>
      <tbody>
        {data.map(item => (
          <tr
            className={styles.styledTableRow}
            data-table-data-row
            data-testid="table-data-row"
            onClick={onRowClick ? () => onRowClick(item) : undefined}
            key={item.key}
          >
            {columns.map(column => {
              const columnAlignment = column.alignment || alignment;

              if (column.renderCell) {
                return (
                  <Fragment key={column.key}>
                    {column.renderCell({
                      item,
                      column,
                      hasScrollbar,
                      alignment: columnAlignment,
                    })}
                  </Fragment>
                );
              }

              return (
                <ColumnTableCell
                  key={column.key}
                  item={item}
                  column={column}
                  alignment={columnAlignment}
                  hasScrollbar={hasScrollbar}
                />
              );
            })}
            {renderActionColumn && (
              <TableCell
                className={styles.actionTableCell}
                alignment={alignment}
                isNumber={false}
                data-revamp-table-action-cell
              >
                <div>{renderActionColumn(item)}</div>
              </TableCell>
            )}
          </tr>
        ))}
      </tbody>
    </RevampTable>
  );
};
